export const SEO_BASE_URL = 'https://www.stocktag.ai';
export const ORGANIZATION_LOGO = `${SEO_BASE_URL}/logo-icon.png`;
export const ORGANIZATION_SUPPORT_EMAIL = 'support@stocktag.ai';
export const DEFAULT_SOCIAL_IMAGE = Object.freeze({
  url: `${SEO_BASE_URL}/home-redesign/studio/light/studio-overview-1440.webp`,
  width: 1440,
  height: 900,
  type: 'image/webp',
  alt: 'Stocktag metadata studio showing the file-to-keyword export workflow'
});
export const SITE_SOCIAL_LINKS = [
  { id: 'facebook', label: 'Facebook', href: 'https://www.facebook.com/profile.php?id=61588401130718' },
  { id: 'instagram', label: 'Instagram', href: 'https://www.instagram.com/stocktag.ai' },
  { id: 'youtube', label: 'YouTube', href: 'https://www.youtube.com/@stocktagai' },
  { id: 'x', label: 'X', href: 'https://x.com/StocktagAI' },
  { id: 'linkedin', label: 'LinkedIn', href: 'https://www.linkedin.com/company/stocktag-ai' }
];
export const ORGANIZATION_SAME_AS = SITE_SOCIAL_LINKS.map((link) => link.href);

export const SEO_DISCOVERY_PATHS = {
  llms: '/llms.txt',
  llmsFull: '/llms-full.txt',
  aiIndex: '/ai-index.json',
  blogRss: '/blog/rss.xml',
  blogJson: '/blog/feed.json'
};

const LOCALIZED_DISCOVERY_MARKDOWN_LANGUAGES = new Set(['tr', 'es', 'fr', 'de', 'it', 'id', 'th']);
const THAI_ONLY_DISCOVERY_MARKDOWN_PATHS = new Set([
  '/ai/markdown/home.md',
  '/ai/markdown/comparisons/alternatives.md',
  '/ai/markdown/legal/privacy.md',
  '/ai/markdown/legal/terms.md'
]);

export const localizeDiscoveryMarkdownPath = (markdownPath = '', language = 'en') => {
  const normalizedPath = String(markdownPath || '').trim();
  if (!normalizedPath) return '';

  const normalizedLanguage = String(language || 'en').trim().toLowerCase().split(/[-_]/)[0] || 'en';
  const absolutePath = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;

  if (!absolutePath.startsWith('/ai/markdown/')) return absolutePath;
  if (!LOCALIZED_DISCOVERY_MARKDOWN_LANGUAGES.has(normalizedLanguage)) return absolutePath;
  if (normalizedLanguage !== 'th' && THAI_ONLY_DISCOVERY_MARKDOWN_PATHS.has(absolutePath)) {
    return absolutePath;
  }

  return absolutePath.replace('/ai/markdown/', `/ai/markdown/${normalizedLanguage}/`);
};

// Separators that only ever divide list items. A comma can also sit inside a
// sentence, so comma lists are checked separately (see splitKeywordSource).
const KEYWORD_LIST_SEPARATOR_PATTERN = /\s*(?:;|\||\u2022|\n)\s*/g;
const KEYWORD_COMMA_PATTERN = /\s*,\s*/g;
const MAX_KEYWORD_WORDS = 8;
const MAX_KEYWORD_LENGTH = 80;
// Sentence punctuation followed by a space or the end: "and iStock.",
// "2026. Compare official pricing". Dots inside tokens such as "Stocktag.ai" pass.
const SENTENCE_PUNCTUATION_PATTERN = /[.!?\u3002\uff01\uff1f\u0964](?:\s|$)/u;
// A comma part that opens with a conjunction belongs to a sentence's
// enumeration ("titles, and descriptions with AI"), not to a keyword list.
const ENUMERATION_CONJUNCTION_PATTERN = /^(?:and|or|then|ve|veya|ya da|y|e|ed|o|u|et|ou|und|oder|dan|atau|\u0e41\u0e25\u0e30|\u0e2b\u0e23\u0e37\u0e2d|\u0914\u0930|\u092f\u093e|\u098f\u09ac\u0982|\u0993|\u09ac\u09be)(?:\s|$)/iu;

const flattenValues = (input) => {
  if (Array.isArray(input)) {
    return input.flatMap((item) => flattenValues(item));
  }
  return [input];
};

const normalizeKeyword = (value) => (
  String(value || '')
    .replace(/\[[^\]]+\]\([^)]+\)/g, '$1')
    .replace(/<[^>]+>/g, ' ')
    .replace(/[*_`>#]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
);

export const toPlainText = (value = '') => normalizeKeyword(value);

export const buildAnchorId = (value, fallback = 'section') => {
  const normalized = toPlainText(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalized || fallback;
};

export const summarizeTextBlock = (value, { maxLength = 160 } = {}) => {
  const text = toPlainText(value).replace(/\s+/g, ' ').trim();
  if (!text) return '';

  const firstSentence = text.match(/[^.!?]+[.!?]?/u)?.[0]?.trim() || text;
  if (firstSentence.length <= maxLength) return firstSentence;

  const slice = firstSentence.slice(0, maxLength + 1);
  const lastSpace = slice.lastIndexOf(' ');
  const clipped = lastSpace > 48 ? slice.slice(0, lastSpace) : slice.slice(0, maxLength);
  return `${clipped.trim().replace(/[.,;:!?-]+$/u, '')}…`;
};

const isKeywordLike = (value) => (
  Boolean(value)
  && value.length <= MAX_KEYWORD_LENGTH
  && value.split(/\s+/).length <= MAX_KEYWORD_WORDS
  && !SENTENCE_PUNCTUATION_PATTERN.test(value)
);

// Splits a source into keyword candidates. Lists ("a, b; c | d") are split;
// prose (descriptions, sentences, enumerations such as "Queue, review, and
// export") is never cut into fragments: such a comma segment is dropped whole.
const splitKeywordSource = (source) => (
  source.split(KEYWORD_LIST_SEPARATOR_PATTERN).flatMap((segment) => {
    const text = normalizeKeyword(segment);
    if (!text) return [];
    if (!text.includes(',')) return [text];
    const parts = text.split(KEYWORD_COMMA_PATTERN).map(normalizeKeyword).filter(Boolean);
    // A colon marks a title ("X vs Y: Pricing, FTP & Workflow"), not a list.
    const isList = !SENTENCE_PUNCTUATION_PATTERN.test(text)
      && !text.includes(':')
      && !parts.some((part) => ENUMERATION_CONJUNCTION_PATTERN.test(part));
    return isList ? parts : [];
  })
);

export const collectSeoKeywords = (...sources) => {
  const seen = new Set();
  const keywords = [];

  flattenValues(sources).forEach((source) => {
    if (typeof source !== 'string') return;

    splitKeywordSource(source).forEach((keyword) => {
      if (!isKeywordLike(keyword)) return;
      const dedupeKey = keyword.toLowerCase();
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      keywords.push(keyword);
    });
  });

  return keywords;
};

export const toKeywordString = (...sources) => collectSeoKeywords(...sources).join(', ');

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Named entities a page can be "about". Only these, and entity objects a caller
// passes explicitly (such as the competitor a comparison page reviews), become
// schema.org `about` Things: free text is never turned into entities. sameAs
// points at each entity's official site.
export const SEO_ENTITY_CATALOG = Object.freeze([
  { name: 'Adobe Stock', sameAs: ['https://stock.adobe.com/'] },
  { name: 'Shutterstock', sameAs: ['https://www.shutterstock.com/'] },
  { name: 'Getty Images', sameAs: ['https://www.gettyimages.com/'] },
  { name: 'iStock', aliases: ['iStock', 'iStockphoto'], sameAs: ['https://www.istockphoto.com/'] },
  { name: 'Alamy', sameAs: ['https://www.alamy.com/'] },
  { name: 'Depositphotos', sameAs: ['https://depositphotos.com/'] },
  { name: 'Dreamstime', sameAs: ['https://www.dreamstime.com/'] },
  { name: 'Freepik', sameAs: ['https://www.freepik.com/'] },
  { name: 'Pond5', sameAs: ['https://www.pond5.com/'] },
  { name: '123RF', sameAs: ['https://www.123rf.com/'] },
  { name: 'Canva', sameAs: ['https://www.canva.com/'] },
  { name: 'Vecteezy', sameAs: ['https://www.vecteezy.com/'] },
  { name: 'Creative Fabrica', sameAs: ['https://www.creativefabrica.com/'] },
  { name: 'Design Bundles', sameAs: ['https://designbundles.net/'] },
  { name: 'MotionElements', aliases: ['MotionElements', 'Motion Elements'], sameAs: ['https://www.motionelements.com/'] },
  {
    name: 'IPTC Photo Metadata Standard',
    aliases: ['IPTC'],
    sameAs: ['https://iptc.org/standards/photo-metadata/iptc-standard/']
  },
  { name: 'Extensible Metadata Platform (XMP)', aliases: ['XMP'] }
].map((entity) => Object.freeze({
  ...entity,
  // No lookbehind: older Safari versions reject it when the RegExp is built.
  pattern: new RegExp(
    `(?:^|[^\\p{L}\\p{N}])(?:${(entity.aliases || [entity.name])
      .map((alias) => escapeRegExp(alias).replace(/\s+/g, '[\\s-]+'))
      .join('|')})(?![\\p{L}\\p{N}])`,
    'iu'
  )
})));

const toThingEntity = ({ name, sameAs }) => ({
  '@type': 'Thing',
  name,
  ...(Array.isArray(sameAs) && sameAs.length ? { sameAs: [...sameAs] } : {})
});

// Builds `about` from the SEO_ENTITY_CATALOG entities that the string sources
// mention (in order of first mention), after any explicit `{ name, sameAs? }`
// objects. Returns [] when nothing matches; SchemaOrg omits an empty `about`.
export const buildThingEntities = (...sources) => {
  const values = flattenValues(sources);
  const explicit = values
    .filter((value) => value && typeof value === 'object' && typeof value.name === 'string' && value.name.trim())
    .map((value) => toThingEntity({ name: normalizeKeyword(value.name), sameAs: value.sameAs }));
  const text = values
    .filter((value) => typeof value === 'string')
    .map(normalizeKeyword)
    .join(' \n ');
  const mentioned = SEO_ENTITY_CATALOG
    .map((entity) => ({ entity, index: text.search(entity.pattern) }))
    .filter(({ index }) => index >= 0)
    .sort((a, b) => a.index - b.index)
    .map(({ entity }) => toThingEntity(entity));

  const seen = new Set();
  return [...explicit, ...mentioned]
    .filter((entity) => {
      const key = entity.name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 16);
};

export const buildItemListSchema = ({
  name,
  description,
  items = [],
  resolveName,
  resolveUrl
} = {}) => {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!list.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    ...(name ? { name } : {}),
    ...(description ? { description } : {}),
    itemListElement: list.map((item, index) => {
      const entry = {
        '@type': 'ListItem',
        position: index + 1,
        name: typeof resolveName === 'function'
          ? resolveName(item, index)
          : normalizeKeyword(item)
      };

      const resolvedUrl = typeof resolveUrl === 'function' ? resolveUrl(item, index) : '';
      if (resolvedUrl) {
        entry.url = resolvedUrl;
      }

      return entry;
    })
  };
};

export const estimateWordCount = (...sources) => {
  const text = flattenValues(sources)
    .map((value) => normalizeKeyword(value))
    .filter(Boolean)
    .join(' ');

  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
};

export const buildSiteDiscoveryLinks = ({ markdownPath = '', language = 'en' } = {}) => {
  const links = [
    {
      rel: 'alternate',
      type: 'text/plain',
      title: 'Stocktag LLM Summary',
      href: `${SEO_BASE_URL}${SEO_DISCOVERY_PATHS.llms}`
    },
    {
      rel: 'alternate',
      type: 'text/plain',
      title: 'Stocktag LLM Full Context',
      href: `${SEO_BASE_URL}${SEO_DISCOVERY_PATHS.llmsFull}`
    },
    {
      rel: 'alternate',
      type: 'application/json',
      title: 'Stocktag AI Index',
      href: `${SEO_BASE_URL}${SEO_DISCOVERY_PATHS.aiIndex}`
    },
    {
      rel: 'alternate',
      type: 'application/rss+xml',
      title: 'Stocktag Blog RSS Feed',
      href: `${SEO_BASE_URL}${SEO_DISCOVERY_PATHS.blogRss}`
    },
    {
      rel: 'alternate',
      type: 'application/feed+json',
      title: 'Stocktag Blog JSON Feed',
      href: `${SEO_BASE_URL}${SEO_DISCOVERY_PATHS.blogJson}`
    }
  ];

  if (markdownPath) {
    const localizedMarkdownPath = localizeDiscoveryMarkdownPath(markdownPath, language);

    links.unshift({
      rel: 'alternate',
      type: 'text/markdown',
      title: 'Stocktag Markdown Summary',
      href: `${SEO_BASE_URL}${localizedMarkdownPath}`
    });
  }

  return links;
};
